import { useCallback, useState } from 'react';

type UseMinimizedChatInputOptions = {
  /** New chat / different `/chat/:id` resets both `minimized` and `hidden`. */
  scopeKey: string;
  /** Start expanded (e.g. on the new-chat page). */
  startExpanded?: boolean;
  /** Files waiting in the box to be sent. The collapsed box doesn't list them. */
  unsentFileCount?: number;
};

export function useMinimizedChatInput({
  scopeKey,
  startExpanded = false,
  unsentFileCount = 0,
}: UseMinimizedChatInputOptions) {
  const [minimized, setMinimized] = useState(!startExpanded && unsentFileCount === 0);
  /** Input fully hidden by the user. Unlike `minimized`, scrolling to the bottom doesn't show it again. */
  const [hidden, setHidden] = useState(false);
  const [prevScopeKey, setPrevScopeKey] = useState(scopeKey);
  const [prevUnsentFileCount, setPrevUnsentFileCount] = useState(unsentFileCount);

  if (scopeKey !== prevScopeKey) {
    setPrevScopeKey(scopeKey);
    setMinimized(!startExpanded);
    setHidden(false);
  }

  // Open whenever a file is added (e.g. restored on a return to the chat) so it isn't hidden.
  if (unsentFileCount !== prevUnsentFileCount) {
    setPrevUnsentFileCount(unsentFileCount);
    if (unsentFileCount > prevUnsentFileCount) {
      setMinimized(false);
      setHidden(false);
    }
  }

  const expandChatInput = useCallback(() => {
    setMinimized(false);
  }, []);

  const collapseChatInput = useCallback(() => {
    setMinimized(true);
  }, []);

  const hideChatInput = useCallback(() => {
    setHidden(true);
  }, []);

  /** Brings the input back fully expanded, however far up the transcript the user still is. */
  const showChatInput = useCallback(() => {
    setHidden(false);
    setMinimized(false);
  }, []);

  return {
    minimized,
    hidden,
    expandChatInput,
    collapseChatInput,
    hideChatInput,
    showChatInput,
  };
}
